import { useTranslation } from 'react-i18next'

import type { LearnArticleBodyProps } from '@/features/aprender/articles'
import {
    DashList,
    LEARN_ESCANDALLO_EXCEL_VIEW_URL,
    LearnArticleLink,
    LearnSeriesNav,
    TipiLink,
} from '@/features/aprender/learnArticleShared'
import { cn } from '@/lib/utils'
import { article as articleUi } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const k = (field: string) => `learn.articles.que-es-un-escandallo.${field}`

const SOURCES: { labelKey: string; fallback: string; href?: string }[] = [
    {
        labelKey: 'sources.1',
        fallback: 'Real Academia Española, Diccionario de la lengua española, «escandallo».',
        href: 'https://dle.rae.es/escandallo',
    },
    {
        labelKey: 'sources.2',
        fallback: 'Real Academia Española, Diccionario de la lengua española, «escandallar».',
        href: 'https://dle.rae.es/escandallar',
    },
    {
        labelKey: 'sources.3',
        fallback: 'Real Academia Española, Diccionario del estudiante, «escandallo».',
        href: 'https://www.rae.es/diccionario-estudiante/escandallo',
    },
    {
        labelKey: 'sources.4',
        fallback:
            'Guías del sector consultadas para contrastar definiciones: Talent Hostelería, Diego Coquillat, Barcelona Culinary Hub, Supercash y Glass & Service.',
    },
    {
        labelKey: 'sources.5',
        fallback: 'Ejemplo del pulpo a la gallega, precios y pérdida de cocción: elaboración propia de Tipi.',
    },
]

export function QueEsUnEscandalloArticle({ article }: LearnArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'creencia', label: t(k('toc.1'), '1. «¿Y eso del escandallo qué es?»') },
        { id: 'definicion', label: t(k('toc.2'), '2. Qué es un escandallo (y qué parte de la respuesta es cierta)') },
        { id: 'no-son', label: t(k('toc.3'), '3. Escandallos: lo que no son') },
        { id: 'etimologia', label: t(k('toc.4'), '4. La palabra viene de una sonda con sebo') },
        { id: 'merma', label: t(k('toc.5'), '5. ¿Basta con sumar lo que lleva el plato?') },
        { id: 'caduca', label: t(k('toc.6'), '6. ¿Cada cuánto caduca un escandallo?') },
        { id: 'semana', label: t(k('toc.7'), '7. Qué cambia en tu semana') },
        { id: 'discrepancias', label: t(k('toc.8'), '8. Documento o cálculo') },
    ]

    const whatRows = [
        {
            line: t(k('table.what.1.line'), 'Ingrediente'),
            detail: t(k('table.what.1.detail'), 'Todo lo que lleva, también el aceite, la sal y la guarnición'),
        },
        {
            line: t(k('table.what.2.line'), 'Cantidad'),
            detail: t(k('table.what.2.detail'), 'Gramos por ración, pesados, no a ojo'),
        },
        {
            line: t(k('table.what.3.line'), 'Precio'),
            detail: t(k('table.what.3.detail'), 'Por kilo o litro, del último albarán'),
        },
        {
            line: t(k('table.what.4.line'), 'Merma'),
            detail: t(k('table.what.4.detail'), 'Lo que se pierde al limpiar, pelar o cocinar'),
        },
        {
            line: t(k('table.what.5.line'), 'Coste por ración'),
            detail: t(k('table.what.5.detail'), 'La suma, ya con la merma aplicada'),
        },
    ]

    return (
        <div className={articleUi.page}>
            <article className={articleUi.shell}>
                <ArticleCoverHero
                    category={article.category}
                    title={article.title}
                    dek={
                        <>
                            <strong>
                                {t(
                                    k('dekStrong'),
                                    'Un escandallo es el cálculo de lo que te cuesta de verdad una ración: cada ingrediente con su peso real, su merma y el precio del último albarán.',
                                )}
                            </strong>{' '}
                            {t(
                                k('dekRest'),
                                'Los escandallos no son una suma que se hace una vez al montar la carta. En nuestro ejemplo, un pulpo a la gallega escandallado sin merma sale a 3,32 € y en realidad cuesta 5,78 €: un food cost del 39,7 % donde creías tener un 22,8 %. Y en cuanto el proveedor sube el pulpo 2 € el kilo, ese escandallo ya no vale.',
                            )}
                        </>
                    }
                    imageSrc={article.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'Cocinero limpiando y pesando producto en cocina para calcular la merma del escandallo',
                    )}
                    byline={
                        <ArticleByline
                            authorName={article.authorName}
                            readingMinutes={article.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />
                <ArticleReadingLayout
                    intro={
                        <div className={articleUi.proseIntro}>
                            <p className={cn(articleUi.p, articleUi.pGapMd, 'text-article-muted-soft')}>
                                {t(k('updated'), 'Publicado el 15 de septiembre de 2026.')}
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <h2 id='creencia' className={articleUi.h2}>
                        {t(k('h2.creencia'), '«¿Y eso del escandallo qué es?»')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.creencia.1'),
                                'Primer día de un cocinero nuevo. Ve la palabra en la libreta del pase y pregunta.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.creencia.2'),
                                'La respuesta que se da en casi todas las cocinas se parece a esta: «Sumar lo que lleva el plato. Se hace cuando montas la carta y ya está».',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.creencia.3'), 'No es una mala respuesta. Es la que nos dieron a casi todos.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(k('p.creencia.4'), 'Pero tiene dos agujeros, y por ellos se escapa el margen.')}
                        </p>
                    </div>

                    <h2 id='definicion' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.definicion'), 'Qué es un escandallo, y qué parte de esa respuesta es cierta')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>
                                {t(
                                    k('p.definicion.1'),
                                    'Un escandallo es el cálculo del coste real de una ración de un plato, ingrediente por ingrediente.',
                                )}
                            </strong>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.definicion.2'),
                                'Sirve para saber cuánto deja cada plato al salir por el pase. Con ese número decides su precio, su gramaje o si se queda en la carta.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.definicion.3'), 'Un escandallo completo recoge, como mínimo, esto:')}
                        </p>
                    </div>

                    <div className={articleUi.tableWrap}>
                        <table className={cn(articleUi.tableFacts, '[&_thead_th]:text-left')}>
                            <caption className='sr-only'>
                                {t(k('table.what.caption'), 'Qué recoge un escandallo completo')}
                            </caption>
                            <thead>
                                <tr>
                                    <th scope='col'>{t(k('table.what.col.line'), 'Línea')}</th>
                                    <th scope='col'>{t(k('table.what.col.detail'), 'Qué recoge')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {whatRows.map(row => (
                                    <tr key={row.line}>
                                        <th scope='row'>{row.line}</th>
                                        <td>{row.detail}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.definicion.4Before'), 'La parte cierta de «sumar lo que lleva el plato»: ')}
                            <strong>
                                {t(k('p.definicion.4Strong'), 'la base de cualquier escandallo es una suma.')}
                            </strong>{' '}
                            {t(k('p.definicion.4After'), 'No hace falta más matemática que multiplicar y sumar.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.definicion.5'),
                                'La parte que falla está en la fila de la merma, en la del precio y en las palabras «ya está».',
                            )}
                        </p>
                    </div>

                    <h2 id='no-son' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.noSon'), 'Escandallos: lo que no son')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.noSon.intro'), 'Tres cosas que se confunden con el escandallo y no lo son.')}
                        </p>
                        <DashList
                            items={[
                                t(
                                    k('p.noSon.1'),
                                    'La ficha técnica. Recoge cómo se elabora y se emplata un plato, y a menudo sus alérgenos. Puede llevar el escandallo dentro, pero una ficha técnica sin precios no te dice nada del margen.',
                                ),
                                t(
                                    k('p.noSon.2'),
                                    'El food cost. Es un porcentaje: el coste de la ración dividido entre el precio de venta sin IVA. Sale del escandallo, no lo sustituye.',
                                ),
                            ]}
                        />
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            <strong>{t(k('p.noSon.3Strong'), 'El coste teórico de la carta.')}</strong>{' '}
                            {t(
                                k('p.noSon.3Before'),
                                'Es lo que deberías gastar si todo el mundo cocinara exactamente como dicen tus escandallos. Lo que gastas de verdad es otra cifra, y esa distancia la contamos en ',
                            )}
                            <LearnArticleLink slug='coste-teorico-vs-coste-real-cocina'>
                                {t(k('linkTeorico'), 'por qué un escandallo impecable no impide que pierdas dinero')}
                            </LearnArticleLink>
                            {t(k('p.noSon.3After'), '.')}
                        </p>
                    </div>

                    <h2 id='etimologia' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.etimologia'), 'La palabra viene de una sonda con sebo')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.etimologia.1'),
                                'Casi todas las guías cuentan que el escandallo era un instrumento para medir la profundidad del mar. Es verdad a medias.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.etimologia.2Before'), 'Según el ')}
                            <a
                                href='https://dle.rae.es/escandallo'
                                className={articleUi.link}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                {t(k('p.etimologia.2Link'), 'Diccionario de la lengua española de la RAE')}
                            </a>
                            {t(
                                k('p.etimologia.2After'),
                                ', la palabra viene del occitano escandall, sonda. El escandallo era la parte de la sonda con una cavidad rellena de sebo, y servía para «reconocer la calidad del fondo del agua» por lo que salía pegado.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.etimologia.3'),
                                'No medía solo cuánto fondo había. Sacaba una muestra para saber cómo era.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.etimologia.4Before'), 'Esa es también la segunda acepción del diccionario: ')}
                            <strong>
                                {t(
                                    k('p.etimologia.4Strong'),
                                    'tomar unas pocas unidades de un conjunto como representativas de la calidad de todo.',
                                )}
                            </strong>{' '}
                            {t(
                                k('p.etimologia.4After'),
                                'El sentido de cálculo del precio de coste llega en la cuarta acepción, marcado como término de comercio, y el Diccionario del estudiante lo ilustra con el precio de un medicamento, no con un plato.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.etimologia.5Before'),
                                'Traducido a cocina: no hace falta escandallar la carta entera para saber cómo está. ',
                            )}
                            <strong>
                                {t(
                                    k('p.etimologia.5Strong'),
                                    'Diez platos bien elegidos, los que más vendes, te dicen cómo está el fondo.',
                                )}
                            </strong>
                        </p>
                    </div>

                    <h2 id='merma' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.merma'), '¿Basta con sumar lo que lleva el plato?')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.merma.1'), 'No. El primer agujero es la merma.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.merma.2Before'), 'Un pulpo a la gallega. PVP de 16 € con IVA, que son ')}
                            <strong>{t(k('p.merma.2Strong'), '14,55 €')}</strong>
                            {t(k('p.merma.2After'), ' sin IVA. Ración de 200 g de pulpo ya cocido.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd, 'italic text-article-muted')}>
                            {t(
                                k('p.merma.note'),
                                'Los precios y la pérdida de cocción son una referencia nuestra para este ejemplo, no precios de mercado: cada proveedor, cada pulpo y cada cocción dan otra cifra.',
                            )}
                        </p>
                    </div>

                    <div className={articleUi.tableWrap}>
                        <table className={cn(articleUi.tableFacts, '[&_thead_th]:text-left')}>
                            <caption className='sr-only'>
                                {t(k('table.sinMerma.caption'), 'Pulpo a la gallega escandallado sin merma')}
                            </caption>
                            <thead>
                                <tr>
                                    <th scope='col'>
                                        {t(k('table.sinMerma.col.item'), 'Pulpo a la gallega, sin merma')}
                                    </th>
                                    <th scope='col'>{t(k('table.sinMerma.col.cost'), 'Coste')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{t(k('table.sinMerma.1'), '200 g de pulpo a 15 €/kg')}</td>
                                    <td>{t(k('table.sinMerma.1c'), '3,00 €')}</td>
                                </tr>
                                <tr>
                                    <td>{t(k('table.sinMerma.2'), 'Patata, aceite, pimentón y sal')}</td>
                                    <td>{t(k('table.sinMerma.2c'), '0,32 €')}</td>
                                </tr>
                                <tr className={articleUi.tableTotal}>
                                    <th scope='row'>{t(k('table.sinMerma.total'), 'Escandallo «de sumar»')}</th>
                                    <td>{t(k('table.sinMerma.totalc'), '3,32 €, food cost del 22,8 %')}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.merma.3Before'),
                                'Pero el pulpo pierde peso al cocerlo. Con una pérdida del 45 %, para servir 200 g cocidos tienes que comprar ',
                            )}
                            <strong>{t(k('p.merma.3Strong'), '364 g')}</strong>
                            {t(k('p.merma.3After'), ' en crudo.')}
                        </p>
                    </div>

                    <div className={articleUi.tableWrap}>
                        <table className={cn(articleUi.tableFacts, '[&_thead_th]:text-left')}>
                            <caption className='sr-only'>
                                {t(k('table.conMerma.caption'), 'Mismo plato escandallado con merma')}
                            </caption>
                            <thead>
                                <tr>
                                    <th scope='col'>{t(k('table.conMerma.col.item'), 'Mismo plato, con merma')}</th>
                                    <th scope='col'>{t(k('table.conMerma.col.cost'), 'Coste')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>{t(k('table.conMerma.1'), '364 g de pulpo crudo a 15 €/kg')}</td>
                                    <td>{t(k('table.conMerma.1c'), '5,46 €')}</td>
                                </tr>
                                <tr>
                                    <td>{t(k('table.conMerma.2'), 'Patata, aceite, pimentón y sal')}</td>
                                    <td>{t(k('table.conMerma.2c'), '0,32 €')}</td>
                                </tr>
                                <tr className={articleUi.tableTotal}>
                                    <th scope='row'>{t(k('table.conMerma.total'), 'Escandallo real')}</th>
                                    <td>{t(k('table.conMerma.totalc'), '5,78 €, food cost del 39,7 %')}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>{t(k('p.merma.4Strong'), 'La diferencia es de 2,46 € por ración.')}</strong>{' '}
                            {t(k('p.merma.4Mid'), 'A 300 raciones al mes, son ')}
                            <strong>{t(k('p.merma.4Month'), '738 €')}</strong>
                            {t(k('p.merma.4YearBefore'), ' de margen que creías tener y no tenías. En un año, ')}
                            <strong>{t(k('p.merma.4Year'), '8.856 €')}</strong>
                            {t(k('p.merma.4YearAfter'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>{t(k('p.merma.5'), 'Con un solo plato.')}</p>
                    </div>

                    <h2 id='caduca' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.caduca'), '¿Cada cuánto caduca un escandallo?')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.caduca.1'), 'El segundo agujero es el «ya está».')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.caduca.2Before'), 'Un escandallo vale lo que vale el precio con el que se hizo. ')}
                            <strong>
                                {t(
                                    k('p.caduca.2Strong'),
                                    'Cuando sube un ingrediente que pesa en el plato, ese escandallo está caducado.',
                                )}
                            </strong>
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.caduca.3'), 'Mismo pulpo. El proveedor lo sube de 15 € a 17 € el kilo.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.caduca.4Before'), 'El coste de la ración pasa de 5,78 € a ')}
                            <strong>{t(k('p.caduca.4Cost'), '6,51 €')}</strong>
                            {t(k('p.caduca.4Mid'), '. El food cost, del 39,7 % al ')}
                            <strong>{t(k('p.caduca.4Fc'), '44,7 %')}</strong>
                            {t(k('p.caduca.4After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.caduca.5Before'),
                                'Frente al escandallo de sumar que tenías colgado en la cocina, ya no son 2,46 € por ración. Son ',
                            )}
                            <strong>{t(k('p.caduca.5Diff'), '3,19 €')}</strong>
                            {t(k('p.caduca.5Mid'), ', unos ')}
                            <strong>{t(k('p.caduca.5Month'), '957 € al mes')}</strong>
                            {t(k('p.caduca.5After'), ' a 300 raciones.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.caduca.6'),
                                'Nadie ha cambiado la receta. Nadie ha servido de más. Ha llegado un albarán.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(k('p.caduca.7Before'), 'Para montar uno desde cero, báscula en mano, tienes ')}
                            <LearnArticleLink slug='como-hacer-un-escandallo-paso-a-paso'>
                                {t(k('linkPasoAPaso'), 'cómo se escandalla una hamburguesa de principio a fin')}
                            </LearnArticleLink>
                            {t(k('p.caduca.7After'), '.')}
                        </p>
                    </div>

                    <h2 id='semana' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.semana'), 'Qué cambia en tu semana')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>{t(k('p.semana.1Strong'), 'Elige tus diez platos.')}</strong>{' '}
                            {t(
                                k('p.semana.1'),
                                'Los diez que más vendes, no los diez que más te gustan. Son tu muestra del fondo.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>{t(k('p.semana.2Strong'), 'Pesa el limpio, no el bruto.')}</strong>{' '}
                            {t(
                                k('p.semana.2'),
                                'En cada ingrediente con merma, pesa lo que entra y lo que queda. Una vez por ingrediente basta para empezar.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            <strong>{t(k('p.semana.3Strong'), 'Mira la fecha de cada precio.')}</strong>{' '}
                            {t(
                                k('p.semana.3'),
                                'Si el precio de un escandallo es anterior al último albarán de ese ingrediente, ese plato está sin escandallar aunque tenga ficha.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.semana.4'),
                                'Y si un plato te sale por encima de lo que aguanta su precio, no es un fallo del escandallo. Es justo para lo que sirve.',
                            )}
                        </p>
                    </div>

                    <h2 id='discrepancias' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.discrepancias'), 'Documento o cálculo: donde las definiciones no coinciden')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.discrepancias.1'), 'No hay una definición única.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.discrepancias.2Before'), 'Hay guías del sector que llaman escandallo al ')}
                            <strong>{t(k('p.discrepancias.2Doc'), 'documento')}</strong>
                            {t(
                                k('p.discrepancias.2Mid'),
                                ', la hoja con ingredientes, gramos y precios. Otras lo llaman al ',
                            )}
                            <strong>{t(k('p.discrepancias.2Calc'), 'cálculo')}</strong>
                            {t(
                                k('p.discrepancias.2After'),
                                '. La RAE lo define como la determinación del precio de coste o de venta de una mercancía a partir de lo que la compone: el acto, no el papel.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.discrepancias.3'),
                                'Nosotros lo usamos como cálculo, porque es lo que caduca. La hoja puede seguir colgada con precios de hace un año.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.discrepancias.4'),
                                'Tampoco hay acuerdo sobre qué entra. Hay cocinas que meten el aceite de la freidora y el pan en cada escandallo, y otras que los tratan aparte. Mientras lo hagas siempre igual, las dos formas sirven.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.discrepancias.5'),
                                'Y la pérdida del 45 % del pulpo es nuestra referencia para el ejemplo. La tuya depende del producto y del tiempo de cocción, y solo la sabes pesando.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.cta.1Before'), 'Si quieres empezar hoy con tus diez platos, abre nuestro ')}
                            <a
                                href={LEARN_ESCANDALLO_EXCEL_VIEW_URL}
                                className={articleUi.link}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                {t(k('p.cta.excel'), 'Excel de escandallos')}
                            </a>
                            {t(
                                k('p.cta.1Mid'),
                                ' y pídenos la copia editable, que es gratis; si prefieres que cada albarán actualice el coste sin tocar una celda, cuéntanoslo y lo miramos en ',
                            )}
                            <TipiLink />
                            {t(k('p.cta.1After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.cta.close'),
                                'Los marineros no echaban la sonda una vez al salir de puerto. La echaban cada vez que cambiaba el fondo.',
                            )}
                        </p>
                    </div>

                    <section className={cn(articleUi.prose, 'mt-[clamp(40px,6vw,64px)]')}>
                        <h2 className={articleUi.h2}>{t(k('sourcesTitle'), 'Fuentes')}</h2>
                        <ul className={articleUi.dashList}>
                            {SOURCES.map(source => (
                                <li key={source.labelKey} className={articleUi.dashItem}>
                                    {source.href ? (
                                        <a
                                            href={source.href}
                                            className={articleUi.link}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                        >
                                            {t(k(source.labelKey), source.fallback)}
                                        </a>
                                    ) : (
                                        t(k(source.labelKey), source.fallback)
                                    )}
                                </li>
                            ))}
                        </ul>
                    </section>
                </ArticleReadingLayout>

                <LearnSeriesNav currentId={article.id} />
            </article>
        </div>
    )
}
